import type { ReactNode } from 'react';
import MuiModal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, CardContent, CardHeader, CardActions, Fade, IconButton, type SxProps } from '@mui/material';

const defaultStyle: SxProps = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 600,
  width: '90%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 0,
};

interface ModalProps {
  title: string;
  description?: string;
  subtitle?: ReactNode;
  avatar?: ReactNode;
  open: boolean;
  handleClose?: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  /** Rendered in the header upper-right, to the left of the close icon. */
  headerActions?: ReactNode;
  size?: 'lg' | 'md' | 'sm';
  height?: string | number;
  minHeight?: string | number;
  maxHeight?: string | number;
  width?: string | number;
  hideCloseIcon?: boolean;
  sx?: SxProps;
  contentSx?: SxProps;
  fullScreen?: boolean;
  stickToRight?: boolean;
  backdropOpacity?: number;
  /** Stop React event bubbling to ancestors (e.g. portaled modals inside clickable list rows). */
  isolateClickPropagation?: boolean;
}

export default function Modal({
  title,
  description,
  subtitle,
  avatar,
  open,
  handleClose,
  children,
  footer,
  headerActions,
  size,
  height,
  minHeight,
  maxHeight,
  width,
  hideCloseIcon,
  sx,
  contentSx,
  fullScreen,
  stickToRight,
  backdropOpacity = 0.5,
  isolateClickPropagation = false,
}: ModalProps) {
  let mergedStyles = { ...defaultStyle, ...sx };

  if (fullScreen) {
    mergedStyles = {
      ...mergedStyles,
      top: 0,
      left: 0,
      transform: 'none',
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100dvh',
      minHeight: '100%',
      borderRadius: 0,
      border: 'none',
    };
  } else {
    if (stickToRight) {
      mergedStyles = {
        ...mergedStyles,
        '@media (max-width: 500px)': {
          left: 'auto',
          right: '16px',
          transform: 'translateY(-50%)',
        },
      };
    }

    switch (size) {
      case 'lg':
        mergedStyles = {
          ...mergedStyles,
          ...(height != null ? { height } : {}),
          maxWidth: width ? width : '800px',
        };
        break;
      case 'md':
        mergedStyles = {
          ...mergedStyles,
          ...(height != null ? { height } : {}),
          maxWidth: width ? width : '550px',
        };
        break;
      case 'sm':
        mergedStyles = {
          ...mergedStyles,
          ...(height != null ? { height } : {}),
          maxWidth: width ? width : '350px',
        };
        break;
    }
  }
  if (width && !fullScreen) {
    // Ensure modal width never exceeds viewport width (with 32px padding on each side)
    // Use min() to respect both the specified width and viewport constraints
    const constrainedWidth =
      typeof width === 'number' ? `min(${width}px, calc(100vw - 64px))` : `min(${width}, calc(100vw - 64px))`;
    mergedStyles = { ...mergedStyles, width: constrainedWidth, maxWidth: constrainedWidth };
  }
  if (minHeight != null) {
    mergedStyles = { ...mergedStyles, minHeight };
  }
  if (maxHeight != null) {
    mergedStyles = { ...mergedStyles, maxHeight };
  }
  return (
    <MuiModal
      // Do not call stopPropagation on the modal root: MUI X pickers (and similar) attach a
      // document-level click listener to accept the draft value and close; blocking the bubble
      // path to document would leave popovers open and ignore the in-progress selection.
      open={open}
      onClose={() => {
        handleClose?.();
      }}
      BackdropProps={{
        sx: {
          backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})`,
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <Card
          sx={mergedStyles}
          onClick={isolateClickPropagation ? (event) => event.stopPropagation() : undefined}
          onMouseDown={isolateClickPropagation ? (event) => event.stopPropagation() : undefined}
        >
          <CardHeader
            title={title}
            subheader={subtitle ?? description}
            avatar={avatar}
            sx={{
              '& .MuiCardHeader-title': {
                fontWeight: 600,
              },
            }}
            action={
              headerActions || !hideCloseIcon ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {headerActions}
                  {!hideCloseIcon ? (
                    <IconButton data-testid="close-modal" onClick={handleClose}>
                      <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                  ) : null}
                </Box>
              ) : undefined
            }
          />
          <CardContent
            sx={{
              pb: 0,
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              ...contentSx,
            }}
          >
            {children}
          </CardContent>
          {footer && (
            <CardActions
              sx={{
                justifyContent: 'flex-end',
                p: 2,
              }}
            >
              {footer}
            </CardActions>
          )}
        </Card>
      </Fade>
    </MuiModal>
  );
}
